import React, { useRef, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { VehiclePartCategoryTreeItem } from '@1po/1po-bff-fe-spec/generated/catalog/category/model/VehiclePartCategoryTreeItem';
import { ROUTER_CATALOG_VEHICLE } from 'app/AppRouter';

import { getLastSearchedVehicleKey, getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { formatCategoryLabelToDataCy, getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import {
  SRoutineVehicleMaintenanceCard,
  SText,
} from 'pages/CatalogPage/DH/CarPartGroupsSection/CarPartGroupItem/CarPartGroupItem.styled';
import { CarPartElementId } from 'pages/CatalogPage/DH/CarPartGroupsSection/CarPartGroupsSection';
import { ScrollWrapper } from 'pages/CatalogPage/DH/CarPartGroupsSection/CarPartGroupsSection.styled';
import { Box, CenterFlex, Flex, Image, sentenceCase, Text, WithTooltip } from 'UI';
import { useExtraLarge } from 'utils';
import { useOffsetTop } from 'utils/hooks/useOffsetTop';
import { RoutineVehicleMaintenanceItemContainer } from './RoutineVehicleMaintenanceGroupCard.styled';
import { routineVehicleMaintenanceItems } from './RoutineVehicleMaintenanceGroupCard.types';
import { useRvmL2Sections } from '../CarPartGroupsSection.utils';

export const BODY_HEIGHT = 514;
export const CARD_HEIGHT = BODY_HEIGHT + 60;
export const BODY_WIDTH = 1200;

interface RoutineVehicleMaintenanceItemProps {
  label: string;
  nodeId: string;
  path: string;
  scale: number;
}

const RoutineVehicleMaintenanceItem = ({ label, nodeId, path, scale }: RoutineVehicleMaintenanceItemProps) => {
  const history = useHistory();
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const currentItem = routineVehicleMaintenanceItems[nodeId];

  if (!currentItem) return null;

  return (
    <WithTooltip title={sentenceCase(label)}>
      <RoutineVehicleMaintenanceItemContainer
        onClick={() => {
          if (vehicleDetail) {
            history.push({
              pathname: `${getCatalogSourceUrl(
                vehicleDetail.catalogSource,
              )}/${vehicleKey}${ROUTER_CATALOG_VEHICLE}/${path}`,
              search: location.search,
            });
          }
        }}
        offsetX={currentItem.offsetX * scale}
        offsetY={currentItem.offsetY * scale}
      >
        <Image
          alt={label}
          src={`catalog/RoutineVehicleMaintenance/${currentItem.src}`}
          width={currentItem.width * scale}
          dataCy={formatCategoryLabelToDataCy(label, 'image')}
        />
      </RoutineVehicleMaintenanceItemContainer>
    </WithTooltip>
  );
};

interface RoutineVehicleMaintenanceGroupCardProps {
  rvmNode: VehiclePartCategoryTreeItem | undefined;
  lvl1Nodes: VehiclePartCategoryTreeItem[];
}

export const RoutineVehicleMaintenanceGroupCard = ({ rvmNode, lvl1Nodes }: RoutineVehicleMaintenanceGroupCardProps) => {
  const { t } = useTranslation();
  const offsetTop = useOffsetTop('RENAULT_CATALOG');
  const isExtraLarge = useExtraLarge();
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const onResize = () => {
      if (containerRef.current) {
        const parentWidth = containerRef.current.offsetWidth;

        const newScale = parentWidth / BODY_WIDTH;
        setScale(newScale);
      }
    };

    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const rvmL2Sections = useRvmL2Sections(rvmNode, lvl1Nodes);
  const l3Items = rvmL2Sections.flatMap((l2) => l2.l3Items);

  return (
    <Box height={CARD_HEIGHT * (scale * 1.2)}>
      <CenterFlex>
        <ScrollWrapper offsetTop={offsetTop} id={`${CarPartElementId}${rvmNode?.nodeId}`}>
          <SRoutineVehicleMaintenanceCard
            title={
              <SText
                type={'h2'}
                cursor={'pointer'}
                disableGutter
                ellipsis
                data-cy={'title-routine-vehicle-maintenance'}
              >
                {sentenceCase(rvmNode?.label ?? '')}
              </SText>
            }
            width={BODY_WIDTH}
          >
            <Flex
              minHeight={BODY_HEIGHT * (scale * 1.2)}
              minWidth={isExtraLarge ? BODY_WIDTH : '100%'}
              direction={'column'}
              data-cy={'content-routine-vehicle-maintenance'}
            >
              <Flex direction={'row'} justify={'flex-end'} maxHeight={70}>
                <Box width={isExtraLarge ? 300 : '100%'}>
                  <Text type={'h1_banner_light'}>{t('catalog.parts.front', 'Front')}</Text>
                </Box>

                <Box width={isExtraLarge ? 500 : '100%'}>
                  <Text type={'h1_banner_light'}>{t('catalog.parts.rear', 'Rear')}</Text>
                </Box>
              </Flex>
              <Flex ref={containerRef}>
                {l3Items?.map(
                  (item) =>
                    item && (
                      <RoutineVehicleMaintenanceItem
                        key={`rvm-${item.nodeId}`}
                        label={item.label}
                        nodeId={item.nodeId}
                        path={item.path}
                        scale={scale}
                      />
                    ),
                )}
              </Flex>
            </Flex>
          </SRoutineVehicleMaintenanceCard>
        </ScrollWrapper>
      </CenterFlex>
    </Box>
  );
};
